import { useContext, useEffect, useState } from 'react';
import styled from '@emotion/styled';
import { ConfigContext } from '@/contextAPI/configContext';
import { checkIfUrlIsValid } from '@/_utils/utils';

interface PosterDisplayProps {
  url: string;
  filename: string;
  height?: number | string;
  width?: number | string;
  className?: string;
}

const PosterDisplay = ({ url, filename, height = 400, width, className }: PosterDisplayProps) => {
  const [isValidUrl, setIsValidUrl] = useState<boolean>(true);
  const { theme } = useContext(ConfigContext);

  useEffect(() => {
    const checkToSeeIfUrlIsValid = async () => {
      const isValid = (await checkIfUrlIsValid(url)) as boolean;
      setIsValidUrl(isValid);
    };
    checkToSeeIfUrlIsValid();
  }, [url]);

  return (
    <Poster height={height} valid={+isValidUrl} width={width} className={className}>
      {isValidUrl ? (
        <>
          <ImageBackground url={url} />
          <ImageHolder>
            <img src={url} alt={filename} />
          </ImageHolder>
        </>
      ) : (
        <ErrorMessage>
          <StyledPreviewPlaceholder src={theme?.preview_placeholder || theme?.logo} alt="Fallback" />
        </ErrorMessage>
      )}
    </Poster>
  );
};

export default PosterDisplay;

const Poster = styled.div<{ height: number | string; valid: number; width?: number | string }>`
  height: ${({ height }) => (typeof height === 'number' ? height + 'px' : height)};
  width: ${({ width }) => (!width ? '100%' : typeof width === 'number' ? width + 'px' : width)};
  position: relative;
  overflow: hidden;
  z-index: 3;
  background-color: ${({ theme }) => theme.palette.common.black};
`;

const ImageBackground = styled.div<{ url: string }>`
  height: 100%;
  width: 100%;
  inset: -10px;
  background: ${({ url }) => `url("${url}") center center / cover no-repeat`};
  filter: blur(5px) saturate(0.8) opacity(0.3);
`;

const ImageHolder = styled.div`
  position: absolute;
  z-index: 2;
  top: 50%;
  left: 50%;
  width: 100%;
  height: 100%;
  img {
    max-width: 90%;
    max-height: 90%;
    display: block;
    transform: translate(-50%, -50%);
    border: ${({ theme }) => `2px solid ${theme.palette.divider + '8a'}`};
    filter: ${({ theme }) =>
      `drop-shadow(0 0 30px ${theme.palette.common.black}) drop-shadow(0 0 30px ${theme.palette.common.black}) drop-shadow(0 0 90px ${theme.palette.primary.main})`};
  }
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 20px;
  color: ${({ theme }) => theme.palette.getContrastText(theme.palette.background.default)};
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: center;
  opacity: 0.8;
  height: 100%;

  span {
    font-size: 1rem;
    margin-left: ${({ theme }) => theme.spacing(1)};
  }
`;

const StyledPreviewPlaceholder = styled.img`
  height: 100%;
  width: 100%;
  object-fit: contain;
  padding: ${({ theme }) => theme.spacing(2)};
`;
